import React, { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, FormHelperText, Stack } from "@mui/material";
import { CCheckbox } from "@/components/Checkbox/CCheckbox";
import { Button, ButtonVariantOption } from "@/components/Button";
import { AcquireBookSelectionSection } from "@/components/AcquireBook/AcquireBookSelectionSection";
import { ConfirmAcquireBookDialog } from "@/components/AcquireBook/ConfirmAcquireBookDialog";
import { AllMattersAnalyzeOptionsCheckbox } from "@/components/AcquireBook/AllMattersAnalyzeOptionsCheckbox";
import {
  convertBookTypeToEng,
  getInitialPictureRequestOptions,
} from "@/utils/utilsAcquireBook";
import { createAcquireBookModalFormSchema } from "@/schemas/acquirebook";
import { type AcquireBookModalFormData } from "@/types/acquirebook";
import { useFeatureFlags } from "@/configs/featureFlag";
import { usePictureCheckOptionsManager } from "@/hooks/usePictureCheckOptionsManager";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import {
  KNOWN_REAL_ESTATE_TYPES,
  type PropertyInfoResponse,
} from "@/features/propertyCodeAcquisition/types";
import { usePropertyCodeAcquireBookForm } from "@/features/propertyCodeAcquisition/hooks/usePropertyCodeAcquireBookForm";
import { useAcquireReuse } from "@/features/propertyCodeAcquisition/hooks/useAcquireReuse";
import { DEFAULT_ALL_MATTERS_ANALYZE_OPTIONS } from "@/features/propertyCodeAcquisition/utils/newAcquireRequest";
import { findUncoveredReusePictures } from "@/features/propertyCodeAcquisition/utils/reuseAcquireRequest";

// 地番系フィールドのダミー値（物件コードでリクエストするため送信しない・スキーマ min(1) 通過用）。
// 共有マネージャの updatePictureCheckOptionsForm はこれらを「フォーム有効」と判定するため
// リユース確認ボタンが活性化される（実際の所在解決はサーバが物件コードから行う）。
const DUMMY_PREFECTURE_ID = "0";
const DUMMY_LOCATION = "-";
const DUMMY_CHIBAN_KAOKU_NUMBERS = ["-"];

// 物件コード専用の取得ダイアログ（フォーム本体）。
//
// 物件コードでリクエストし地番はサーバが解決するため、地番入力 UI は持たない。
// 下層の取得選択 UI 部品（AcquireBookSelectionSection / CheckboxesToSelectBookOptions /
//   AllMattersAnalyzeOptionsCheckbox / ConfirmAcquireBookDialog）は地番フィールドに依存しないため import 共有する。
//   不動産種別は物件情報から確定するため UI で選択させず、図面活性は種別連動（getInitialPictureRequestOptions）で初期化する。

interface IProps {
  /** 物件情報（不動産種別・物件コードの取得元） */
  propertyInfo: PropertyInfoResponse;
  /** クローズ処理（キャンセル・取得成功時のダイアログ閉じる） */
  onClose: () => void;
  /** 取得成功時のコールバック（履歴の mutate 再取得）。 */
  onAcquireSuccess?: () => void;
}

export const PropertyCodeAcquireBookForm: React.FC<IProps> = ({
  propertyInfo,
  onClose,
  onAcquireSuccess,
}) => {
  const { ownerInfoViaScraping, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();

  // 読み取り専用ユーザは取得（課金）を実行できない。確認ダイアログの取得ボタンを非活性にする。
  // 他の登記取得画面（AcquireBookFormPage / AcquireMultipleBooks 等）と同じ共通制御に揃える。
  const isReadOnly = useReadOnlyUser();

  // 表示名（"土地"/"建物"/"区分建物"）→ 既存ダイアログが使う種別コード（LAND/BUILDING/CONDOMINIUM）へ変換。
  const realEstateTypeEng = convertBookTypeToEng(propertyInfo.realEstateType);

  // 未知の不動産種別は convertBookTypeToEng が暗黙で LAND にフォールバックするため、運用で観測する。
  useEffect(() => {
    if (!KNOWN_REAL_ESTATE_TYPES.has(propertyInfo.realEstateType)) {
      Sentry.captureMessage(
        `未知の不動産種別を LAND としてフォールバックしました: ${propertyInfo.realEstateType}`,
        "warning"
      );
    }
  }, [propertyInfo.realEstateType]);

  // 取得実行 hook（物件コード配下エンドポイント）。
  const acquireBookForm = usePropertyCodeAcquireBookForm(
    propertyInfo.propertyCode
  );

  // リユース確認（物件コード配下エンドポイント・インライン反映）。
  const {
    acquireReuse,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  } = useAcquireReuse(propertyInfo.propertyCode);
  const isReuseDataFetched = reuseInfoGetMutateAPIResponseList.length > 0;

  // 確認ダイアログ表示・キャンセルボタン無効化の状態。
  // 登記取得の送信中はキャンセルボタンを無効化し、二重送信を防ぐ。
  const [openModalFlag, setOpenModalFlag] = useState(false);
  const [disableConfirmModalCancelButton, setDisableConfirmModalCancelButton] =
    useState(false);

  // resolver は isReuseDataFetched が変わった時のみ再生成する（毎レンダリングの生成を避ける）。
  const resolver = useMemo(
    () => zodResolver(createAcquireBookModalFormSchema(isReuseDataFetched)),
    [isReuseDataFetched]
  );

  // defaultValues はマウント時に一度だけ評価される。getInitialPictureRequestOptions の
  // ネストオブジェクト生成が useWatch 駆動の頻繁な再レンダリングごとに無駄に走るのを避けるため、
  // 種別（realEstateTypeEng）が変わらない限り再生成しないよう memo 化する。
  const defaultFormValues = useMemo<AcquireBookModalFormData>(
    () => ({
      // 種別は物件情報から確定（UI で選択させない）。
      realEstateTypeEng,
      // 地番系は物件コードでリクエストするため送信しない。スキーマの min(1) を満たすためのダミー（UI には出さない・送信時無視）。
      prefectureId: DUMMY_PREFECTURE_ID,
      prefectureName: "",
      location: DUMMY_LOCATION,
      chibanKaokuNumbers: DUMMY_CHIBAN_KAOKU_NUMBERS,
      // 図面活性が種別連動で初期化される（建物図面＝建物/区分建物で活性・土地で非活性）。
      // defaultValues はマウント時に一度だけ評価され、リユース確認後の再評価はされないため
      // hasReuseData は常に初期状態の false を明示する。
      pictureCheckOptions: getInitialPictureRequestOptions({
        realEstateTypeEng,
        hasReuseData: false,
      }),
      // 識別名・経過日数は UI に出さない。
      // 識別名は契約上空文字可のため "" を送信し、経過日数は物件コードでリクエストするため送信しないが
      // スキーマ（必須 number min(0)）を満たすダミー 0 を入れる。
      identifyName: "",
      pastDays: 0,
      withOwnerAnalyze: false,
      withAllMattersAnalyze: false,
      allMattersAnalyzeOptions: DEFAULT_ALL_MATTERS_ANALYZE_OPTIONS,
      // 閉鎖登記簿は本フェーズ対象外（UI に出さず false 固定）。
      requestClosureBook: false,
    }),
    [realEstateTypeEng]
  );

  const reactHookForm = useForm<AcquireBookModalFormData>({
    mode: "all",
    defaultValues: defaultFormValues,
    resolver,
  });

  // watch（フォーム全体購読で全項目変更ごとに再レンダリング）ではなく useWatch でフィールド単位購読にし、
  // 関係ないフィールドの変更による不要な再レンダリングを抑える。
  const { control } = reactHookForm;
  const pictureCheckOptions = useWatch({
    control,
    name: "pictureCheckOptions",
  });
  const withAllMattersAnalyze = useWatch({
    control,
    name: "withAllMattersAnalyze",
  });
  const allMattersAnalyzeOptions = useWatch({
    control,
    name: "allMattersAnalyzeOptions",
  });
  const requestClosureBook = useWatch({ control, name: "requestClosureBook" });

  // 図面チェックの活性制御（種別連動）とリユース表示を管理する共通 hook を流用する。
  const {
    getReuseData,
    getReusePictures,
    updatePictureCheckOptionsForm,
    disabledReuseRequest,
  } = usePictureCheckOptionsManager({
    reactHookForm,
    // 種別は物件情報から確定済み。リユース確認ボタンの活性はマネージャの判定に委ねる。
    initialDisabledReuseRequest: false,
    initialPictureCheckOptions: pictureCheckOptions,
    reuseInfoGetMutateAPIResponseList,
    setReuseInfoGetMutateAPIResponseList,
  });

  // 「取得済・リユース登記/図面を確認する」押下時の処理。
  // リユース確認 API（物件コード配下エンドポイント）を呼び、結果を同一フォームにインライン反映する。
  const handleAcquireReuseClick = async (
    cancelLoading: () => void
  ): Promise<void> => {
    const result = await acquireReuse(cancelLoading);
    // 確認失敗時（ok=false）はフォーム状態を保持し初期化しない（0 件成功と区別）。
    // エラートーストは acquireReuse 内の handlePropertyCodeApiError が表示済み（二重表示しない）。
    if (!result.ok) return;
    // レスポンスを取得方式ラジオへ反映する（地番系はダミー値で「有効」判定にする＝地番は送らず物件コードでリクエストする）。
    updatePictureCheckOptionsForm(
      realEstateTypeEng,
      DUMMY_PREFECTURE_ID,
      DUMMY_LOCATION,
      DUMMY_CHIBAN_KAOKU_NUMBERS,
      result.data.length === 0, // リユース可能が無い場合は取得済登記の選択肢を出さない
      true // チェック状態を保持する
    );
  };

  // 確認ダイアログの「取得する」押下時の処理。
  const handleAcquireButtonClick = async (
    _event: React.MouseEvent<HTMLButtonElement>,
    cancelLoading: () => void
  ): Promise<void> => {
    await reactHookForm.handleSubmit(
      (data) => {
        const reusePictures = getReusePictures();

        // リユース確認後のみ: チェック済み種別が payload でカバーされるか検証する。
        // 漏れ（取得方式未確定で黙って落ちる種別）があれば送信を中止し、やり直しを促す。
        if (isReuseDataFetched) {
          const uncovered = findUncoveredReusePictures({
            requestOptions: data.pictureCheckOptions,
            reusePictures,
          });
          if (uncovered.length > 0) {
            toast.error(
              "選択内容に不整合があります。リユース確認をやり直してください。"
            );
            Sentry.captureMessage(
              `リユース選択の不整合（payload に乗らない種別）: ${uncovered.join(
                ", "
              )}`,
              "warning"
            );
            cancelLoading();
            return;
          }
        }

        acquireBookForm.handleSubmit({
          requestOptions: data.pictureCheckOptions,
          withOwnerAnalyze: data.withOwnerAnalyze,
          withAllMattersAnalyze: data.withAllMattersAnalyze,
          identificationName: data.identifyName,
          allMattersAnalyzeOptions: data.allMattersAnalyzeOptions,
          reusePictures,
          cancelLoading,
          setDisableConfirmModalCancelButton,
          // 成功時は確認ダイアログとフォームを閉じ、履歴を再取得する。
          onSuccess: () => {
            setOpenModalFlag(false);
            onAcquireSuccess?.();
            onClose();
          },
        });
      },
      () => {
        cancelLoading();
      }
    )();
  };

  return (
    <>
      <Stack spacing={2} sx={{ mt: "8px" }}>
        <Box sx={{ mt: 3 }}>
          <FormProvider {...reactHookForm}>
            <AcquireBookSelectionSection
              disabled={false}
              isDataFetched={isReuseDataFetched}
              disabledReuseRequestButton={disabledReuseRequest}
              onClickReuseInfoGetButton={(_event, cancelLoading) => {
                void handleAcquireReuseClick(cancelLoading);
              }}
              getReuseData={getReuseData}
              handleCustomOptionChange={(event, key) => {
                if (key === "SHOYUSHAJIKO" && !event) {
                  reactHookForm.setValue("withOwnerAnalyze", false);
                }
                if (key === "ZENBUJIKO" && !event) {
                  reactHookForm.setValue("withAllMattersAnalyze", false);
                }
              }}
            />
          </FormProvider>
        </Box>
      </Stack>
      {(ownerInfoViaScraping || displayCheckboxAllMattersAnalyze) && (
        <Box display="flex" flexDirection="column" gap={1}>
          {ownerInfoViaScraping && (
            <CCheckbox
              control={reactHookForm.control}
              name="withOwnerAnalyze"
              label="所有者事項の取得と同時に内容を解析する"
              disabled={!pictureCheckOptions.SHOYUSHAJIKO.checked}
            />
          )}
          {displayCheckboxAllMattersAnalyze && (
            <>
              <CCheckbox
                control={reactHookForm.control}
                name="withAllMattersAnalyze"
                label="全部事項の取得と同時に内容を解析する"
                disabled={!pictureCheckOptions.ZENBUJIKO.checked}
              />
              {withAllMattersAnalyze && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <AllMattersAnalyzeOptionsCheckbox
                    disabled={
                      !pictureCheckOptions.ZENBUJIKO.checked ||
                      requestClosureBook
                    }
                    value={allMattersAnalyzeOptions}
                    onChange={(value) => {
                      reactHookForm.setValue(
                        "allMattersAnalyzeOptions",
                        value,
                        {
                          shouldValidate: true,
                        }
                      );
                    }}
                  />
                  {reactHookForm.formState.errors.allMattersAnalyzeOptions
                    ?.message != null && (
                    <FormHelperText error sx={{ textAlign: "center" }}>
                      {
                        reactHookForm.formState.errors.allMattersAnalyzeOptions
                          ?.message
                      }
                    </FormHelperText>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
      <Box
        sx={{
          mx: "auto",
          my: 4,
          display: "flex",
          justifyContent: "center",
          gap: "0 1em",
        }}
      >
        <Button
          variant={ButtonVariantOption.Outlined}
          onClick={() => {
            onClose();
          }}
          disabled={disableConfirmModalCancelButton}
          label={"キャンセル"}
        />
        <Button
          variant={ButtonVariantOption.Contained}
          onClick={() => {
            void (async () => {
              const isValid = await reactHookForm.trigger();
              if (isValid) {
                setOpenModalFlag(true);
              }
            })();
          }}
          label={"取得する"}
        />
      </Box>

      {/* 確認ダイアログ（共有部品） */}
      <ConfirmAcquireBookDialog
        open={openModalFlag}
        setOpen={setOpenModalFlag}
        pictureCheckOptions={pictureCheckOptions}
        handleAcquireButtonClick={handleAcquireButtonClick}
        disableConfirmModalCancelButton={disableConfirmModalCancelButton}
        isReadOnly={isReadOnly}
      />
    </>
  );
};
